/*
Copyright (C) 2023-2026 QuantumNous

This program is free software: you can redistribute it and/or modify
it under the terms of the GNU Affero General Public License as
published by the Free Software Foundation, either version 3 of the
License, or (at your option) any later version.

This program is distributed in the hope that it will be useful,
but WITHOUT ANY WARRANTY; without even the implied warranty of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License
along with this program. If not, see <https://www.gnu.org/licenses/>.

For commercial licensing, please contact support@quantumnous.com
*/
import { useState } from 'react'
import { Copy, Check, ExternalLink, Play, ImageIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { Dialog } from '@/components/dialog'
import { StatusBadge } from '@/components/status-badge'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import { ScrollArea } from '@/components/ui/scroll-area'
import { useCopyToClipboard } from '@/hooks/use-copy-to-clipboard'
import { formatTimestampToDate } from '@/lib/format'

import { TASK_STATUS, TASK_ACTIONS } from '../../constants'
import { taskStatusMapper } from '../../lib/mappers'
import type { TaskLog } from '../../types'

// Video actions that produce video content
const VIDEO_ACTIONS = [
  TASK_ACTIONS.GENERATE,
  TASK_ACTIONS.TEXT_GENERATE,
  TASK_ACTIONS.FIRST_TAIL_GENERATE,
  TASK_ACTIONS.REFERENCE_GENERATE,
  TASK_ACTIONS.REMIX_GENERATE,
]

// Video URL extensions
const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.m3u8']

// Image URL extensions
const IMAGE_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg']

function isVideoUrl(url: string): boolean {
  const lowerUrl = url.toLowerCase()
  return VIDEO_EXTENSIONS.some((ext) => lowerUrl.includes(ext))
}

function isImageUrl(url: string): boolean {
  const lowerUrl = url.toLowerCase()
  return IMAGE_EXTENSIONS.some((ext) => lowerUrl.includes(ext))
}

function isVideoTask(action: string): boolean {
  return VIDEO_ACTIONS.includes(action as (typeof VIDEO_ACTIONS)[number])
}

interface TaskResultDialogProps {
  log: TaskLog
  open: boolean
  onOpenChange: (open: boolean) => void
}

function DetailRow({
  label,
  value,
  mono,
}: {
  label: string
  value: React.ReactNode
  mono?: boolean
}) {
  return (
    <div className='grid min-w-0 grid-cols-[6rem_minmax(0,1fr)] gap-2 text-sm sm:grid-cols-[8rem_minmax(0,1fr)] sm:gap-3'>
      <span className='text-muted-foreground min-w-0 text-xs'>{label}</span>
      <span
        className={`max-w-full min-w-0 text-xs break-all ${mono ? 'font-mono' : ''}`}
      >
        {value || '-'}
      </span>
    </div>
  )
}

function JsonPreview({
  data,
  label,
  copyToClipboard,
  copiedText,
}: {
  data: unknown
  label: string
  copyToClipboard: (text: string) => void
  copiedText: string | null
}) {
  if (!data) return null

  const jsonString =
    typeof data === 'string' ? data : JSON.stringify(data, null, 2)
  const displayText =
    typeof data === 'string'
      ? (() => {
          try {
            return JSON.stringify(JSON.parse(data), null, 2)
          } catch {
            return data
          }
        })()
      : jsonString

  return (
    <div className='space-y-1.5'>
      <Label className='text-xs font-semibold'>{label}</Label>
      <div className='bg-muted/50 relative rounded-md border p-3'>
        <Button
          variant='ghost'
          size='sm'
          className='absolute top-2 right-2 h-7 w-7 p-0'
          onClick={() => copyToClipboard(jsonString)}
          title='Copy to clipboard'
        >
          {copiedText === jsonString ? (
            <Check className='size-3.5 text-green-600' />
          ) : (
            <Copy className='size-3.5' />
          )}
        </Button>
        <pre className='max-h-60 min-w-0 overflow-auto pr-8 font-mono text-[11px] leading-relaxed break-all whitespace-pre-wrap'>
          {displayText}
        </pre>
      </div>
    </div>
  )
}

function VideoPlayerDialog({
  videoUrl,
  taskId,
  open,
  onOpenChange,
}: {
  videoUrl: string
  taskId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('Video Preview')}
      description={t('Play the generated video')}
      contentClassName='sm:max-w-4xl'
      contentHeight='auto'
      bodyClassName='p-0'
    >
      <div className='aspect-video w-full bg-black'>
        <video
          src={videoUrl}
          controls
          autoPlay
          className='size-full'
          title={t('Video for task {{taskId}}', { taskId })}
        >
          {t('Your browser does not support the video tag.')}
        </video>
      </div>
    </Dialog>
  )
}

function ImagePreviewDialog({
  imageUrl,
  taskId,
  open,
  onOpenChange,
}: {
  imageUrl: string
  taskId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t('Image Preview')}
      description={t('View the generated image')}
      contentClassName='sm:max-w-4xl'
      contentHeight='auto'
      bodyClassName='p-2'
    >
      <div className='flex items-center justify-center'>
        <img
          src={imageUrl}
          alt={t('Image for task {{taskId}}', { taskId })}
          className='max-h-[70vh] max-w-full object-contain'
        />
      </div>
    </Dialog>
  )
}

export function TaskResultDialog({
  log,
  open,
  onOpenChange,
}: TaskResultDialogProps) {
  const { t } = useTranslation()
  const { copiedText, copyToClipboard } = useCopyToClipboard({ notify: false })
  const [videoDialogOpen, setVideoDialogOpen] = useState(false)
  const [imageDialogOpen, setImageDialogOpen] = useState(false)
  const [sourceVideoDialogOpen, setSourceVideoDialogOpen] = useState(false)
  const [sourceImageDialogOpen, setSourceImageDialogOpen] = useState(false)

  const isSuccess = log.status === TASK_STATUS.SUCCESS
  const resultUrl = log.result_url || log.fail_reason

  // Determine if this is a video task
  const canPlayVideo =
    isSuccess &&
    resultUrl &&
    (isVideoTask(log.action) || isVideoUrl(resultUrl))

  // Determine if this is an image URL
  const canShowImage = isSuccess && resultUrl && isImageUrl(resultUrl)

  // Build video URL - use proxy endpoint for task-based URLs
  const videoUrl = canPlayVideo
    ? resultUrl!.startsWith('http')
      ? resultUrl!
      : `/v1/videos/${log.task_id}/content`
    : null

  // Image URL - direct URL
  const imageUrl = canShowImage ? resultUrl : null

  // Parse data field if it's a JSON string
  let parsedData: unknown = null
  if (log.data) {
    try {
      parsedData =
        typeof log.data === 'string' ? JSON.parse(log.data) : log.data
    } catch {
      parsedData = log.data
    }
  }

  // Extract source result URL from metadata
  const sourceResultUrl =
    parsedData &&
    typeof parsedData === 'object' &&
    parsedData !== null &&
    'metadata' in parsedData &&
    typeof (parsedData as Record<string, unknown>).metadata === 'object' &&
    (parsedData as Record<string, unknown>).metadata !== null
      ? ((parsedData as Record<string, unknown>).metadata as Record<string, unknown>)
          .url
      : null

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={onOpenChange}
        title={t('Task Result Details')}
        description={t('View the complete task result information')}
        contentClassName='sm:max-w-2xl'
        contentHeight='auto'
        bodyClassName='space-y-4'
      >
        <ScrollArea className='max-h-[70vh] pr-4'>
          <div className='space-y-4 py-2'>
            {/* Basic Info Section */}
            <div className='space-y-2'>
              <Label className='text-sm font-semibold'>{t('Basic Info')}</Label>
              <div className='bg-muted/30 space-y-2 rounded-md border p-3'>
                <DetailRow label={t('Task ID')} value={log.task_id} mono />
                <DetailRow
                  label={t('Status')}
                  value={
                    <StatusBadge
                      label={t(
                        taskStatusMapper.getLabel(
                          log.status,
                          log.status || 'Unknown'
                        )
                      )}
                      variant={taskStatusMapper.getVariant(log.status)}
                      size='sm'
                      copyable={false}
                    />
                  }
                />
                <DetailRow label={t('Platform')} value={t(log.platform)} />
                <DetailRow
                  label={t('Submit Time')}
                  value={formatTimestampToDate(log.submit_time, 'seconds')}
                />
                {log.finish_time && (
                  <DetailRow
                    label={t('Finish Time')}
                    value={formatTimestampToDate(log.finish_time, 'seconds')}
                  />
                )}
                {log.progress && (
                  <DetailRow label={t('Progress')} value={log.progress} />
                )}
                {log.progress_message_en && (
                  <DetailRow
                    label={t('Progress Message')}
                    value={log.progress_message_en}
                  />
                )}
              </div>
            </div>

            {/* Result URL Section */}
            {resultUrl && (
              <div className='space-y-2'>
                <Label className='text-sm font-semibold'>
                  {isSuccess ? t('Result URL') : t('Fail Reason')}
                </Label>
                <div
                  className={`relative rounded-md border p-3 ${isSuccess ? 'bg-muted/30' : 'border-red-200 bg-red-50 dark:border-red-900 dark:bg-red-950/20'}`}
                >
                  {/* Action buttons - top right */}
                  <div className='absolute top-2 right-2 flex items-center gap-1'>
                    {canPlayVideo && videoUrl && (
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-7 w-7 p-0'
                        onClick={() => setVideoDialogOpen(true)}
                        title={t('Play video')}
                      >
                        <Play className='size-3.5 text-green-600' />
                      </Button>
                    )}
                    {canShowImage && imageUrl && (
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-7 w-7 p-0'
                        onClick={() => setImageDialogOpen(true)}
                        title={t('View image')}
                      >
                        <ImageIcon className='size-3.5 text-blue-600' />
                      </Button>
                    )}
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-7 w-7 p-0'
                      onClick={() => copyToClipboard(resultUrl)}
                      title={t('Copy to clipboard')}
                    >
                      {copiedText === resultUrl ? (
                        <Check className='size-3.5 text-green-600' />
                      ) : (
                        <Copy className='size-3.5' />
                      )}
                    </Button>
                  </div>
                  {isSuccess && resultUrl.startsWith('http') ? (
                    <div className='flex items-start gap-2 pr-20'>
                      <a
                        href={resultUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-primary hover:underline'
                      >
                        <ExternalLink className='size-4 shrink-0' />
                      </a>
                      <span className='min-w-0 break-all font-mono text-xs'>
                        {resultUrl}
                      </span>
                    </div>
                  ) : (
                    <p
                      className={`pr-20 text-xs leading-relaxed break-all ${!isSuccess ? 'text-red-600 dark:text-red-400' : ''}`}
                    >
                      {resultUrl}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Source Result URL Section */}
            {sourceResultUrl && typeof sourceResultUrl === 'string' && (
              <div className='space-y-2'>
                <Label className='text-sm font-semibold'>
                  {t('Source Result URL')}
                </Label>
                <div className='bg-muted/30 relative rounded-md border p-3'>
                  {/* Action buttons - top right */}
                  <div className='absolute top-2 right-2 flex items-center gap-1'>
                    {isVideoUrl(sourceResultUrl) && (
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-7 w-7 p-0'
                        onClick={() => setSourceVideoDialogOpen(true)}
                        title={t('Play video')}
                      >
                        <Play className='size-3.5 text-green-600' />
                      </Button>
                    )}
                    {isImageUrl(sourceResultUrl) && (
                      <Button
                        variant='ghost'
                        size='sm'
                        className='h-7 w-7 p-0'
                        onClick={() => setSourceImageDialogOpen(true)}
                        title={t('View image')}
                      >
                        <ImageIcon className='size-3.5 text-blue-600' />
                      </Button>
                    )}
                    <Button
                      variant='ghost'
                      size='sm'
                      className='h-7 w-7 p-0'
                      onClick={() => copyToClipboard(sourceResultUrl)}
                      title={t('Copy to clipboard')}
                    >
                      {copiedText === sourceResultUrl ? (
                        <Check className='size-3.5 text-green-600' />
                      ) : (
                        <Copy className='size-3.5' />
                      )}
                    </Button>
                  </div>
                  {sourceResultUrl.startsWith('http') ? (
                    <div className='flex items-start gap-2 pr-20'>
                      <a
                        href={sourceResultUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='text-primary hover:underline'
                      >
                        <ExternalLink className='size-4 shrink-0' />
                      </a>
                      <span className='min-w-0 break-all font-mono text-xs'>
                        {sourceResultUrl}
                      </span>
                    </div>
                  ) : (
                    <p className='pr-20 text-xs leading-relaxed break-all'>
                      {sourceResultUrl}
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Data Section */}
            {parsedData && (
              <JsonPreview
                data={parsedData}
                label={t('Result Data')}
                copyToClipboard={copyToClipboard}
                copiedText={copiedText}
              />
            )}

            {/* Other Section */}
            {log.other && (
              <JsonPreview
                data={log.other}
                label={t('Additional Info')}
                copyToClipboard={copyToClipboard}
                copiedText={copiedText}
              />
            )}
          </div>
        </ScrollArea>
      </Dialog>

      {/* Video Player Dialog */}
      {videoUrl && (
        <VideoPlayerDialog
          videoUrl={videoUrl}
          taskId={log.task_id}
          open={videoDialogOpen}
          onOpenChange={setVideoDialogOpen}
        />
      )}

      {/* Image Preview Dialog */}
      {imageUrl && (
        <ImagePreviewDialog
          imageUrl={imageUrl}
          taskId={log.task_id}
          open={imageDialogOpen}
          onOpenChange={setImageDialogOpen}
        />
      )}

      {/* Source Video Player Dialog */}
      {sourceResultUrl && typeof sourceResultUrl === 'string' && isVideoUrl(sourceResultUrl) && (
        <VideoPlayerDialog
          videoUrl={sourceResultUrl}
          taskId={log.task_id}
          open={sourceVideoDialogOpen}
          onOpenChange={setSourceVideoDialogOpen}
        />
      )}

      {/* Source Image Preview Dialog */}
      {sourceResultUrl && typeof sourceResultUrl === 'string' && isImageUrl(sourceResultUrl) && (
        <ImagePreviewDialog
          imageUrl={sourceResultUrl}
          taskId={log.task_id}
          open={sourceImageDialogOpen}
          onOpenChange={setSourceImageDialogOpen}
        />
      )}
    </>
  )
}
